import {
  AlertBar as RemoteAlertBar,
  AlertBarColors,
  getFontCss,
  styleConstants,
} from '@paywithextend/components'
import { base_colorRaisedSurface } from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { connect, useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import Toast from 'src/app/Toast/Toast'
import CobrandLogo from 'src/components/CobrandLogo/CobrandLogo'
import DevToolsButton from 'src/components/DevToolsButton/DevToolsButton'
import SystemAlertPopup from 'src/components/SystemAlertPopup/SystemAlertPopup'
import { Environments } from 'src/configurations'
import { SupportedBrands } from 'src/enums'
import Trns, { useTranslate } from 'src/localization/Trns'
import { unauthedAppRoute } from 'src/navigation/appRoutes'
import { Rdrct } from 'src/navigation/Lnk'
import { signinUrl } from 'src/navigation/urls'
import { RootState } from 'src/redux/createRootReducer'
import {
  selectAlertMessageFromState,
  selectIsAlertBarOpenFromState,
  selectMessageCountFromState,
} from 'src/redux/selectors/app.selectors'
import { selectAlertBarPushdown } from 'src/redux/selectors/css.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectBrandName,
  selectEnvironmentFromState,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const Unauthed: React.FC = () => {
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const isAlertBarOpen = useSelector(selectIsAlertBarOpenFromState)
  const alertMessage = useSelector(selectAlertMessageFromState)
  const messageCount = useSelector(selectMessageCountFromState)
  const alertBarPushdown = useSelector(selectAlertBarPushdown)
  const appName = useSelector(selectAppName)
  const brandName = useSelector(selectBrandName)
  const env = useSelector(selectEnvironmentFromState)
  const translate = useTranslate()

  const [showSystemAlertPopup, setShowSystemAlertPopup] =
    useState<boolean>(false)

  return (
    <div
      css={css`
        min-height: 100vh;
        width: 100%;
        background-color: ${styleConstants.colors.gray_light};
      `}
    >
      {isAlertBarOpen && (
        <>
          <RemoteAlertBar
            alertMessage={alertMessage}
            messageCount={messageCount}
            messageTitle={<Trns source={'System alert'} />}
            link={
              <span
                onClick={(): void => setShowSystemAlertPopup(true)}
                onKeyPress={(): void => setShowSystemAlertPopup(true)}
                role="button"
                tabIndex={0}
                aria-label={'view-status-button'}
                css={css`
                  cursor: pointer;
                `}
              >
                <Trns source={'View status'} />
              </span>
            }
            barColor={AlertBarColors.cream}
            css={css`
              top: ${alertBarPushdown}px;
            `}
          />
          {showSystemAlertPopup && (
            <SystemAlertPopup
              onClose={(): void => setShowSystemAlertPopup(false)}
            />
          )}
        </>
      )}
      {(brandName === SupportedBrands.Pnc ||
        brandName === SupportedBrands.Bmo ||
        brandName === SupportedBrands.Jpm) && (
        <div
          css={css`
            margin-left: 30px;
            ${brandName === SupportedBrands.Jpm &&
            css`
              margin-left: 48px;
              margin-top: 48px;
            `}
            display: flex;
            flex-direction: row;
            align-items: center;
            position: fixed;
            z-index: 10;
          `}
        >
          {brandName === SupportedBrands.Pnc ? (
            <div
              css={css`
                background-color: ${base_colorRaisedSurface};
                border-bottom-left-radius: ${spacing(1)};
                border-bottom-right-radius: ${spacing(1)};
                box-shadow: 0 3px 4px rgba(0, 0, 0, 0.1);
                padding: ${spacing(4)} ${spacing(3)} ${spacing(3)};
                line-height: 0;
                img {
                  display: block;
                  width: 77px;
                  height: 12px;
                }
              `}
            >
              <CobrandLogo brandName={brandName} />
            </div>
          ) : (
            <CobrandLogo brandName={brandName} />
          )}
          {brandName !== SupportedBrands.Jpm && (
            <div
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                margin-top: 10px;
                margin-left: 10px;
              `}
            >
              {translate('In collaboration with Extend')}
            </div>
          )}
        </div>
      )}
      <Helmet>
        <title>{`${appName}`}</title>
        <meta
          name="description"
          content={`Log into ${appName} to create, send, and manage virtual cards for all your business payments.`}
        />
      </Helmet>
      <Switch location={calculatedBackgroundLocation}>
        {unauthedAppRoute.subRoutes.map((route, i) => (
          <Route key={i} {...route.routeProps}>
            {route.component}
          </Route>
        ))}
        <Route path={'/'}>
          <Rdrct to={signinUrl()} />
        </Route>
      </Switch>
      {env !== Environments.Prod && (
        <div
          css={css`
            position: fixed;
            bottom: 5px;
            right: 5px;
            transform: scale(0.4);
            transform-origin: right bottom;
          `}
        >
          <DevToolsButton />
        </div>
      )}
      <Toast />
    </div>
  )
}

// TODO: this is NOT NECESSARY but for some reason useSelector returns a stale value,
// even when checking the values in the equality comparison and ensuring that a === b is false
export default connect(
  (state: RootState) => ({
    bgl: selectCalculatedBackgroundLocation(state),
  }),
  {},
)(Unauthed)
